import { pickOnImageCopy } from "@shared/angleCopy";
import type { CopyAssets } from "@shared/schema";
import { MoreHorizontal, Trash2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

export interface AngleCardItem {
  id: number;
  name: string;
  hookHeadline: string;
  copyAssets?: CopyAssets;
  description: string;
  angleCategory: string;
  adStyle: string;
  emotionalDriver: string;
  status: "approved" | "edited" | "archived";
  confidenceScore: number | null;
  // Optional — only present on COMPETITOR-stage angles, hydrated server-side.
  competitorName?: string;
}

interface AngleCardProps {
  angle: AngleCardItem;
  onClick: () => void;
  onArchive: () => void;
  onViewSource: () => void;
  onGenerateAd?: () => void;
  onRegenerate?: () => void;
  regenerating?: boolean;
  /** View-only contexts (e.g. the legacy brand-level angles view) — hides
   * the kebab's "Generate ad" item. Additive, default false. */
  hideGenerateAction?: boolean;
  /** v6.80 — full-width single-column contexts (the chat Angles doc): no
   * truncation, the whole name/hook/description read without opening. */
  expanded?: boolean;
}

export function AngleCard({ angle, onClick, onArchive, onViewSource, onGenerateAd, hideGenerateAction = false, expanded = false, onRegenerate, regenerating = false }: AngleCardProps) {
  const copy = pickOnImageCopy(angle);
  return (
    <Card
      onClick={onClick}
      className="cursor-pointer hover:shadow-md transition-shadow p-4 flex flex-col gap-3 h-full"
      data-testid={`angle-card-${angle.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className={expanded ? "text-xs text-muted-foreground" : "text-xs text-muted-foreground truncate"} title={angle.name}>
            {angle.name}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">

          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-6 w-6">
                <MoreHorizontal className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
              {onGenerateAd && !hideGenerateAction && (
                <DropdownMenuItem disabled={regenerating} onSelect={onGenerateAd} data-testid={`generate-ad-${angle.id}`}>
                  Generate ad
                </DropdownMenuItem>
              )}
              {onRegenerate && <DropdownMenuItem disabled={regenerating} onSelect={onRegenerate} data-testid={`regenerate-angle-${angle.id}`}>
                {regenerating ? 'Regenerating…' : 'Regenerate angle'}
              </DropdownMenuItem>}
              <DropdownMenuItem onSelect={onViewSource}>View source</DropdownMenuItem>
              <DropdownMenuItem disabled={regenerating} onSelect={onArchive} className="text-destructive" data-testid={`delete-angle-${angle.id}`}>
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className={expanded ? "text-lg font-semibold leading-snug" : "text-sm font-semibold line-clamp-2"}>
        {regenerating ? "Regenerating this concept…" : copy.headline}
      </div>

      <div className={expanded ? "text-xs text-muted-foreground flex-1" : "text-xs text-muted-foreground line-clamp-2 flex-1"}>
        {copy.subheadline ?? angle.description}
      </div>

      {angle.competitorName && (
        <div className="text-xs text-muted-foreground italic" data-testid="competitor-source-badge">
          Inspired by <span className="font-medium not-italic text-foreground">{angle.competitorName}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-1">
        <Badge variant="secondary" className="text-xs">{angle.angleCategory}</Badge>
        <Badge variant="outline" className="text-xs">{angle.adStyle}</Badge>

      </div>

      <div className="flex items-center justify-between text-xs">
        <Badge
          variant={angle.status === "edited" ? "default" : "secondary"}
          className="text-xs capitalize"
        >
          {angle.status}
        </Badge>
        <span className="text-primary font-medium">View Copy →</span>
      </div>
    </Card>
  );
}
